/**
 * The words a suggested child password is built from: one adjective, one noun.
 *
 * Every word here is read aloud by a parent and typed by a child, so the rules
 * are about the ear and the keyboard, not the dictionary:
 *
 *   - four to seven letters, lowercase a–z only
 *   - spelled the way it sounds — no silent letters, no spellings a parent
 *     might reasonably write two ways (grey/gray, donut/doughnut)
 *   - no homophones a listener could mistake for another word (bear/bare)
 *   - nothing with a second meaning — as slang, as a schoolyard taunt, or in a
 *     pairing with any word in the other list, since every adjective can
 *     precede every noun
 *   - each word in one list only, so colours live with the adjectives
 *
 * The lengths and the no-duplicates rule are enforced by password.test.ts.
 * Tone is a judgement call: friendly, playful, the kind of thing a
 * seven-year-old would be pleased to have as a password.
 */

export const ADJECTIVES = [
  // Colours
  'purple',
  'orange',
  'yellow',
  'green',
  'silver',
  'golden',
  'scarlet',
  'violet',
  'indigo',
  'amber',
  'crimson',
  'teal',
  'pink',
  'ruby',
  'cobalt',
  // Moods and manners
  'happy',
  'lucky',
  'jolly',
  'joyful',
  'cheeky',
  'silly',
  'witty',
  'brave',
  'clever',
  'gentle',
  'kind',
  'noble',
  'proud',
  'plucky',
  'perky',
  'peppy',
  'jazzy',
  'groovy',
  'funky',
  'giddy',
  'dizzy',
  'sleepy',
  'dreamy',
  'mellow',
  'lively',
  'chirpy',
  'bouncy',
  'bubbly',
  'jumpy',
  'wild',
  'zany',
  'goofy',
  'loopy',
  'quirky',
  'cheery',
  'upbeat',
  'dandy',
  'spiffy',
  'nifty',
  'snazzy',
  'dapper',
  'loyal',
  'polite',
  'curious',
  'giggly',
  'playful',
  // Speed and power
  'super',
  'mega',
  'ultra',
  'hyper',
  'turbo',
  'rapid',
  'quick',
  'swift',
  'speedy',
  'zippy',
  'snappy',
  'nimble',
  'agile',
  'brisk',
  'sturdy',
  'epic',
  'magic',
  'royal',
  'heroic',
  'daring',
  // Sizes, shapes and textures
  'giant',
  'tiny',
  'mini',
  'jumbo',
  'teeny',
  'little',
  'small',
  'grand',
  'huge',
  'round',
  'curly',
  'wiggly',
  'wobbly',
  'squishy',
  'fluffy',
  'fuzzy',
  'silky',
  'pointy',
  'bumpy',
  'lumpy',
  'shiny',
  'glossy',
  'sparkly',
  'glowing',
  'sleek',
  'crispy',
  'crunchy',
  'chewy',
  'twinkly',
  // Weather, ground and sky
  'sunny',
  'snowy',
  'frosty',
  'chilly',
  'windy',
  'misty',
  'foggy',
  'stormy',
  'rainy',
  'cloudy',
  'breezy',
  'toasty',
  'muddy',
  'polar',
  'dusty',
  'sandy',
  'rocky',
  'rusty',
  'mossy',
  'leafy',
  'grassy',
  'salty',
  'cosmic',
  'lunar',
  'solar',
  // Flavours
  'minty',
  'crumbly',
  'tangy',
  'tasty',
  'yummy',
  'cheesy',
  'sugary',
  'sweet',
  'sour',
  'peachy',
  'lemony',
  'buttery',
  'jammy',
  'fizzy',
  'zingy',
] as const

export const NOUNS = [
  // Animals
  'tiger',
  'panda',
  'koala',
  'otter',
  'zebra',
  'shark',
  'robin',
  'eagle',
  'bunny',
  'puppy',
  'kitten',
  'turtle',
  'dragon',
  'dolphin',
  'penguin',
  'parrot',
  'falcon',
  'gecko',
  'moose',
  'badger',
  'bison',
  'camel',
  'cobra',
  'donkey',
  'ferret',
  'hamster',
  'lemur',
  'lizard',
  'lobster',
  'octopus',
  'orca',
  'panther',
  'pelican',
  'pony',
  'puffin',
  'python',
  'rabbit',
  'raven',
  'sloth',
  'snail',
  'spider',
  'squid',
  'stork',
  'swan',
  'toad',
  'seal',
  'trout',
  'unicorn',
  'wombat',
  'mantis',
  'gerbil',
  'goose',
  'newt',
  'frog',
  'wolf',
  'alpaca',
  'tadpole',
  'ladybug',
  'firefly',
  'possum',
  // Space
  'rocket',
  'comet',
  'planet',
  'galaxy',
  'meteor',
  'nebula',
  'saturn',
  'cosmos',
  'orbit',
  'star',
  'nova',
  'rover',
  'crater',
  'pluto',
  'aurora',
  // Food
  'apple',
  'mango',
  'lemon',
  'grape',
  'waffle',
  'pancake',
  'pretzel',
  'pizza',
  'noodle',
  'popcorn',
  'cupcake',
  'nacho',
  'jelly',
  'maple',
  'kiwi',
  'olive',
  'pumpkin',
  'potato',
  'tomato',
  'candy',
  'bagel',
  'pasta',
  'toast',
  'burger',
  'salsa',
  'pepper',
  'radish',
  'papaya',
  'apricot',
  'custard',
  // Nature and weather
  'river',
  'ocean',
  'cloud',
  'storm',
  'forest',
  'meadow',
  'canyon',
  'volcano',
  'thunder',
  'breeze',
  'pebble',
  'coral',
  'ember',
  'frost',
  'rainbow',
  'sunset',
  'lagoon',
  'jungle',
  'tundra',
  'summit',
  'valley',
  'garden',
  'blossom',
  'acorn',
  'cactus',
  // Toys, machines and heroes
  'robot',
  'pixel',
  'laser',
  'gizmo',
  'gadget',
  'widget',
  'spark',
  'bolt',
  'wire',
  'magnet',
  'puzzle',
  'marble',
  'kite',
  'crayon',
  'bubble',
  'scooter',
  'wagon',
  'engine',
  'wizard',
  'ninja',
] as const
